import { METHOD_MEANING_LINES } from '../../lib/onboardingCatalogs';
import { cn } from '../../utils/cn';

import { MethodPercentageSliders } from './MethodPercentageSliders';

const GROUP_BAR_COLORS = ['#7C9E6F', '#C9A46B', '#7C93A8', '#4F6879', '#8A6D3B', '#78716C'];

const TONE_COLORS = {
  need: '#7C9E6F',
  want: '#C9A46B',
  save: '#7C93A8',
  muted: '#A8A29E',
};

function MethodBar({ groups }) {
  return (
    <div className="my-3 flex h-3 overflow-hidden rounded-md">
      {groups.map((group, index) => (
        <span
          key={group.name}
          style={{
            width: `${group.target_percentage}%`,
            background: group.color || GROUP_BAR_COLORS[index % GROUP_BAR_COLORS.length],
          }}
          className="h-full"
        />
      ))}
    </div>
  );
}

function MeaningLegend({ presetId, groups }) {
  const lines = METHOD_MEANING_LINES[presetId];
  if (lines?.length) {
    return (
      <div className="flex flex-col gap-2">
        {lines.map((line, index) => (
          <div
            key={`${presetId}-${index}`}
            className="type-body-sm flex items-center gap-space-2 leading-snug text-foreground/90"
          >
            <i
              className="h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ background: TONE_COLORS[line.tone] || TONE_COLORS.muted }}
              aria-hidden
            />
            <span>
              {line.bold ? <b className="font-semibold">{line.bold}</b> : null}
              {line.text}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {groups.map((group, index) => (
        <div
          key={group.name}
          className="type-body-sm flex items-center gap-space-2 leading-snug text-muted-foreground"
        >
          <i
            className="h-2.5 w-2.5 shrink-0 rounded-sm"
            style={{
              background: group.color || GROUP_BAR_COLORS[index % GROUP_BAR_COLORS.length],
            }}
            aria-hidden
          />
          <span>
            <b className="font-bold">{group.target_percentage}%</b> {group.name.toLowerCase()}
          </span>
        </div>
      ))}
    </div>
  );
}

export function MethodPresetCard({
  preset,
  selected,
  onSelect,
  recommended = false,
  collapsedCopy,
  className,
  percentages,
  onPercentageChange,
  total,
  isValid,
}) {
  const baseGroups = (preset.groups || []).map((g) => ({
    ...g,
    target_percentage: Number(g.target_percentage),
  }));

  const barGroups =
    selected && percentages?.length
      ? baseGroups.map((group, index) => ({
          ...group,
          target_percentage: Number(percentages[index] ?? group.target_percentage),
        }))
      : baseGroups;

  return (
    <div
      className={cn(
        'w-full rounded-ds-xl border-[1.5px] border-border bg-card p-space-4 text-left transition',
        'hover:border-muted-foreground/30',
        selected &&
          'border-sage-600 shadow-[0_0_0_3px] shadow-sage-50 dark:border-sage-500 dark:shadow-sage-950/60',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onSelect(preset.id)}
        className="flex w-full items-center gap-space-2-5 text-left"
        aria-pressed={selected}
      >
        <span
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-ds-full border-2 border-border',
            selected && 'border-sage-600 dark:border-sage-500',
          )}
          aria-hidden
        >
          {selected ? <span className="h-2.5 w-2.5 rounded-ds-full bg-sage-600" /> : null}
        </span>
        <span className="type-title-card flex-1 font-display font-semibold text-foreground">
          {preset.name}
        </span>
        {recommended ? (
          <span className="type-chip rounded-ds-md border border-sage-200 bg-sage-50 px-space-2 py-0.5 font-bold text-sage-700 dark:border-sage-800 dark:bg-sage-950/40 dark:text-sage-300">
            Recomendado
          </span>
        ) : null}
      </button>

      {!selected && collapsedCopy ? (
        <p className="type-body-sm mt-space-2 leading-snug text-muted-foreground">{collapsedCopy}</p>
      ) : null}

      {selected ? (
        <div>
          <MethodBar groups={barGroups} />
          <MeaningLegend presetId={preset.id} groups={barGroups} />
          {percentages && onPercentageChange ? (
            <MethodPercentageSliders
              className="mt-3"
              groups={baseGroups}
              percentages={percentages}
              onPercentageChange={onPercentageChange}
              total={total}
              isValid={isValid}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
